/*
=============================================
   COMPETITION CASE LIBRARY (student view)         Phase 4, 2026-09-17
=============================================
Design: COMPETITION-CASE-LIBRARY-DESIGN-2026-09-17.md. API: server/routes/library.js.

Two states behind one hash route, the same shape as Training and the class list:
`#library` is the path, `#library/<slug>` is one case. The router in
scripts/web-app.js splits on the slash and hands the tail to show().

UNLIKE Training, this FETCHES. Training is static published coursework that is
identical for every student; the library carries per-student state (what you have
attempted, what you have scored, what you have unlocked) and per-class access, so
it cannot be a content file.

⚠️ THE SERVER DECIDES WHAT IS VISIBLE. This never hides an answer key that the
API returned: the API does not return one until the student has unlocked it.
Anything this file "hides" is presentation only, which is why the Unlock button
re-fetches rather than optimistically revealing.

⚠️ A 404 on /me/library means NO ACCESS, not an error. The library is a class
option, so a student whose classes all have it off sees a plain explanation and
no menu noise.
*/
import { api } from "../scripts/api.js";

const DOWNLOAD_TIMEOUT_MS = 60000;

const TIER_NAMES = {
   1: "Tier 1 · Foundations",
   2: "Tier 2 · JV",
   3: "Tier 3 · Varsity",
   4: "Tier 4 · Accessible pro",
   5: "Tier 5 · Pro",
   6: "Tier 6 · Hard pro",
};

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => (
   { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

const pct = (v) => (v === null || v === undefined ? null : `${Math.round(Number(v) * 100)}%`);

function clock(secs) {
   const s = Math.max(0, Math.floor(secs || 0));
   return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
}

class AppLibrary extends HTMLElement {
   connectedCallback() {
      // The router calls show() on every route change but not before the first
      // one lands, so paint here too or the tab is blank until you navigate away.
      this._downloading = new Set();
      this.paint();
   }

   disconnectedCallback() { this._stopTimer(); }

   show(param) {
      this.slug = param || null;
      this.paint();
   }

   async paint() {
      this._stopTimer();
      const scroller = this.closest("app-content");
      if (scroller) scroller.scrollTop = 0;
      this.innerHTML = `<section class="lib-root"><p class="lib-loading">Loading the case library...</p></section>`;
      try {
         if (this.slug) await this.paintCase();
         else await this.paintPath();
      } catch (err) {
         console.error("library paint failed:", err);
         this.innerHTML = `<section class="lib-root">
            <p class="lib-empty">The case library did not load. Try again in a moment.</p></section>`;
      }
   }

   /* ---------------------------------------------------------------- the path */

   async paintPath() {
      const res = await api("/me/library");
      /*
      ⚠️ THREE DIFFERENT THINGS, and they used to look identical (2026-09-17).
      404 means the API is not deployed yet; 503 means its tables are missing;
      `access: false` means the student's classes all have the library off. The
      first two are ours to fix and the third is the instructor's, so a page that
      blames the student for a missing deploy sends them to the wrong person.
      */
      if (res.status === 404 || res.status === 503) return this.paintUnavailable();
      if (!res.ok) throw new Error(`list ${res.status}`);
      const data = await res.json();
      if (data.access === false) return this.paintNoAccess();
      this._tiers = data.tiers || [];

      const all = this._tiers.flatMap((t) => t.cases);
      const divisions = [...new Set(all.map((c) => c.division).filter(Boolean))];
      const comps = [...new Set(all.map((c) => c.competition).filter(Boolean))];
      const done = all.filter((c) => c.attempts > 0).length;

      this.innerHTML = `
         <section class="lib-root">
            <header class="lib-mast">
               <p class="lib-kicker">Training</p>
               <h1>Competition Case Library</h1>
               <p class="lib-lede">Real MECC and MEWC cases, ordered easiest to hardest. Work one
                  cold, or take it on the clock. The answer key, the solution and the walkthrough
                  videos unlock when you finish a timed attempt, or whenever you decide you want them.</p>
               <p class="lib-stats"><span>${all.length} cases</span><span>${done} attempted</span>
                  <span>${all.filter((c) => c.unlocked).length} unlocked</span></p>
            </header>
            <div class="lib-filters">
               <input type="search" id="lib-q" placeholder="Search a case or a skill" autocomplete="off">
               <select id="lib-div"><option value="">Every division</option>
                  ${divisions.map((d) => `<option>${esc(d)}</option>`).join("")}</select>
               <select id="lib-comp"><option value="">Every competition</option>
                  ${comps.map((c) => `<option>${esc(c)}</option>`).join("")}</select>
               <label class="lib-check"><input type="checkbox" id="lib-new"> Not attempted yet</label>
            </div>
            <div id="lib-path"></div>
         </section>`;

      this.renderPath();
      for (const id of ["lib-q", "lib-div", "lib-comp", "lib-new"]) {
         const el = this.querySelector(`#${id}`);
         el.addEventListener(id === "lib-q" ? "input" : "change", () => this.renderPath());
      }
   }

   renderPath() {
      const q = (this.querySelector("#lib-q")?.value || "").trim().toLowerCase();
      const div = this.querySelector("#lib-div")?.value || "";
      const comp = this.querySelector("#lib-comp")?.value || "";
      const onlyNew = this.querySelector("#lib-new")?.checked;

      const keep = (c) => (!div || c.division === div)
         && (!comp || c.competition === comp)
         && (!onlyNew || c.attempts === 0)
         && (!q || `${c.title} ${c.skill_tags || ""} ${c.competition || ""}`.toLowerCase().includes(q));

      const html = this._tiers.map((t) => {
         const cases = t.cases.filter(keep);
         if (!cases.length) return "";
         return `<section class="lib-tier">
            <h2>${esc(TIER_NAMES[t.tier] || `Tier ${t.tier}`)}<span>${cases.length}</span></h2>
            <div class="lib-grid">${cases.map((c) => this.card(c)).join("")}</div>
         </section>`;
      }).join("");

      this.querySelector("#lib-path").innerHTML = html
         || `<p class="lib-empty">No case matches that. Clear the filters to see the whole path.</p>`;
   }

   card(c) {
      const best = pct(c.best_score);
      const status = c.attempts === 0
         ? `<span class="lib-pill is-new">Not started</span>`
         : `<span class="lib-pill is-done">Best ${best}</span>`;
      return `
         <a class="lib-card" href="#library/${esc(c.slug)}">
            <span class="lib-card-top">
               <span class="lib-badge">${esc(c.division || "Case")}</span>
               ${c.competition ? `<span class="lib-badge is-soft">${esc(c.competition)} ${esc(c.year || "")}</span>` : ""}
            </span>
            <span class="lib-card-title">${esc(c.title)}</span>
            ${c.skill_tags ? `<span class="lib-skills">${esc(c.skill_tags)}</span>` : ""}
            <span class="lib-card-foot">
               ${status}
               <span class="lib-meta">${c.est_minutes || 30} min${c.videos
                  ? ` · ${c.videos} video${c.videos === 1 ? "" : "s"}${c.unlocked ? "" : " (locked)"}` : ""}</span>
            </span>
         </a>`;
   }

   paintUnavailable() {
      this.innerHTML = `
         <section class="lib-root">
            <header class="lib-mast">
               <h1>Competition Case Library</h1>
               <p class="lib-lede">The library is not switched on for this platform yet. Nothing is
                  wrong with your account: the server side is still being deployed. Try again later,
                  or tell your instructor you saw this.</p>
            </header>
         </section>`;
   }

   paintNoAccess() {
      this.innerHTML = `
         <section class="lib-root">
            <header class="lib-mast">
               <h1>Competition Case Library</h1>
               <p class="lib-lede">The case library is open to students at schools and on teams that
                  have been given access, and to classes whose instructor has switched it on. Your
                  account does not have it yet. Ask your instructor, and every past case in the library
                  appears here.</p>
            </header>
         </section>`;
   }

   /* ---------------------------------------------------------------- one case */

   async paintCase() {
      const res = await api(`/me/library/${encodeURIComponent(this.slug)}`);
      if (res.status === 404) {
         // A stale or hand-typed slug, or no access. The path is always a useful
         // destination, and it explains the access case for itself.
         location.replace("#library");
         return;
      }
      if (!res.ok) throw new Error(`case ${res.status}`);
      const data = await res.json();
      const c = data.case;
      this._case = c;
      this._run = data.run;

      const videos = (data.resources || []).filter((r) => r.kind === "video_link");
      const files = (data.resources || []).filter((r) => r.kind !== "video_link");
      const open = (data.resources || []).filter((r) => r.released);
      const locked = (data.resources || []).filter((r) => !r.released);

      this.innerHTML = `
         <section class="lib-root">
            <a class="lib-back" href="#library">&larr; The whole path</a>
            <header class="lib-mast">
               <p class="lib-kicker">${esc(TIER_NAMES[c.tier] || "")} · ${esc(c.competition || "")} ${esc(c.year || "")}</p>
               <h1>${esc(c.title)}</h1>
               ${c.summary ? `<p class="lib-lede">${esc(c.summary)}</p>` : ""}
               <p class="lib-stats">
                  <span>${esc(c.division || "Case")}</span>
                  <span>${c.est_minutes || 30} min</span>
                  <span>${Math.round((c.duration_secs || 1800) / 60)} min on the clock</span>
                  ${c.attempts ? `<span>${c.attempts} attempt${c.attempts === 1 ? "" : "s"}</span>` : ""}
                  ${c.best_score !== null ? `<span>Best ${pct(c.best_score)}</span>` : ""}
               </p>
            </header>

            ${c.skill_tags ? `<section class="lib-panel">
               <h2>Before you start</h2>
               <p class="lib-skills">${esc(c.skill_tags)}</p>
               <p class="lib-note">Rusty on one of these? The written guides are under
                  <a href="student.html#training/skill-guides">Training &rsaquo; Skill Guides</a>.</p>
            </section>` : ""}

            <section class="lib-panel lib-actions" id="lib-actions"></section>

            <section class="lib-panel">
               <h2>Answer materials${c.unlocked ? "" : " <span class=\"lib-lock\">locked</span>"}</h2>
               ${c.unlocked
                  ? `<p class="lib-note">Unlocked ${new Date(c.unlocked_at).toLocaleDateString()}${
                        c.unlock_method === "timed_run" ? ", by finishing a timed attempt" : ""}.</p>`
                  : `<p class="lib-note">The answer key, the solution and ${videos.length || "the"}
                       walkthrough video${videos.length === 1 ? "" : "s"} open when you finish a timed
                       attempt. You can also unlock them now, and your scores after that point are
                       marked as post-unlock in class reports.</p>
                     <button type="button" class="lib-btn is-ghost" id="lib-unlock">Unlock the answers</button>`}
               ${open.length ? `<ul class="lib-res">${open.map((r) => this.resource(r)).join("")}</ul>` : ""}
               ${!c.unlocked && locked.length
                  ? `<ul class="lib-res is-locked">${locked.map((r) =>
                       `<li><span class="lib-res-title">${esc(r.title)}</span>
                            <span class="lib-meta">locked</span></li>`).join("")}</ul>`
                  : ""}
            </section>

            <section class="lib-panel">
               <h2>My work on this case</h2>
               <p class="lib-note">
                  ${c.attempts
                     ? `${c.attempts} recorded attempt${c.attempts === 1 ? "" : "s"},
                        ${c.timed_attempts} on the clock. Best ${pct(c.best_score)}${
                        c.unlocked && c.best_score_pre_unlock !== null
                           ? `, best before you unlocked the answers ${pct(c.best_score_pre_unlock)}` : ""}.`
                     : "Nothing recorded yet. Download the case and your work grades as you go, the same as any assignment."}
               </p>
            </section>
         </section>`;

      this.paintActions();
      this.querySelector("#lib-unlock")?.addEventListener("click", () => this.unlock());
      this.addEventListener("click", (e) => {
         const dl = e.target.closest("[data-res]");
         if (dl) this.downloadResource(Number(dl.dataset.res), dl);
      });
   }

   resource(r) {
      if (r.kind === "video_link" || r.kind === "link") {
         return `<li><a class="lib-res-title" href="${esc(r.url)}" target="_blank" rel="noopener">${esc(r.title)}</a>
                     <span class="lib-meta">${r.kind === "video_link" ? "video" : "link"}</span></li>`;
      }
      return `<li><span class="lib-res-title">${esc(r.title)}</span>
                  <button type="button" class="lib-btn is-small" data-res="${r.resource_id}">Download</button></li>`;
   }

   /* The two buttons, plus the live clock when an attempt is running. */
   paintActions() {
      const c = this._case;
      const run = this._run;
      const box = this.querySelector("#lib-actions");
      if (!box) return;

      if (run) {
         box.innerHTML = `
            <h2>Attempt in progress</h2>
            <p class="lib-clock" id="lib-clock">${clock(run.remaining_secs)}</p>
            <p class="lib-note">Your score is whatever the add-in has graded when the clock stops.
               It ends by itself when the time is up.</p>
            <button type="button" class="lib-btn" id="lib-end">End the attempt now</button>`;
         this.querySelector("#lib-end").addEventListener("click", () => this.endRun(run.run_id));
         this._startTimer(run);
         return;
      }

      box.innerHTML = `
         <h2>Work this case</h2>
         <div class="lib-btn-row">
            ${c.can_download
               ? `<button type="button" class="lib-btn" id="lib-dl">Download the case</button>`
               : `<span class="lib-note">This case has no file on the platform yet.</span>`}
            ${c.can_download
               ? `<button type="button" class="lib-btn is-ghost" id="lib-start">Start a timed attempt
                    (${Math.round((c.duration_secs || 1800) / 60)} min)</button>` : ""}
         </div>
         <p class="lib-note">Practice is untimed and unlimited: download it, work it, and the add-in
            grades as you type. A timed attempt runs a real competition clock and unlocks the answers
            when it ends.</p>`;

      this.querySelector("#lib-dl")?.addEventListener("click", (e) => this.downloadCase(e.currentTarget));
      this.querySelector("#lib-start")?.addEventListener("click", () => this.startRun());
   }

   _startTimer(run) {
      this._stopTimer();
      const ends = run.ends_at ? new Date(run.ends_at).getTime() : null;
      if (!ends) return;
      this._timer = setInterval(() => {
         const left = Math.round((ends - Date.now()) / 1000);
         const el = this.querySelector("#lib-clock");
         if (!el) return this._stopTimer();
         el.textContent = clock(left);
         // ⚠️ The server owns the clock; this is a display. When it reaches zero
         // we re-read rather than declaring the attempt over ourselves.
         if (left <= 0) { this._stopTimer(); this.paint(); }
      }, 1000);
   }

   _stopTimer() { if (this._timer) { clearInterval(this._timer); this._timer = null; } }

   async startRun() {
      const c = this._case;
      const mins = Math.round((c.duration_secs || 1800) / 60);
      if (!confirm(`Start the clock? You get ${mins} minutes, and this attempt starts from an empty `
                 + `score, so work in a fresh download of the case. Finishing it unlocks the answers.`)) return;
      const res = await api(`/me/library/${encodeURIComponent(this.slug)}/runs`, { method: "POST" });
      if (!res.ok) { alert("Could not start the attempt. Try again in a moment."); return; }
      this.paint();
   }

   async endRun(runId) {
      if (!confirm("End the attempt now? Your score is frozen as it stands, and the answers unlock.")) return;
      const res = await api(`/me/runs/${runId}/library-submit`, { method: "POST" });
      if (!res.ok) { alert("Could not end the attempt."); return; }
      this.paint();
   }

   async unlock() {
      if (!confirm("Unlock the answer key, the solution and the walkthrough videos?\n\n"
                 + "Scores you record on this case after now are marked as post-unlock in class reports.")) return;
      const res = await api(`/me/library/${encodeURIComponent(this.slug)}/unlock`, { method: "POST" });
      if (!res.ok) { alert("Could not unlock the answers."); return; }
      this.paint();
   }

   /* Downloads: one in flight per target, a timeout, and the button says so.
      Same three fixes as the class download (student-class-detail.js). */
   async fetchFile(url, key, btn) {
      if (this._downloading.has(key)) return null;
      this._downloading.add(key);
      const label = btn ? btn.textContent : null;
      if (btn) { btn.disabled = true; btn.textContent = "Downloading..."; }
      const ctrl = new AbortController();
      const timer = setTimeout(() => ctrl.abort(), DOWNLOAD_TIMEOUT_MS);
      try {
         return await api(url, { signal: ctrl.signal });
      } catch (err) {
         alert(err?.name === "AbortError"
            ? "That took too long. Check your connection and try again."
            : "Couldn't download the file.");
         return null;
      } finally {
         clearTimeout(timer);
         this._downloading.delete(key);
         if (btn) { btn.disabled = false; if (label !== null) btn.textContent = label; }
      }
   }

   save(res, blob, fallback) {
      const url = URL.createObjectURL(blob);
      const cd = res.headers.get("Content-Disposition") || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const a = document.createElement("a");
      a.href = url;
      a.download = m ? m[1] : fallback;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
   }

   async downloadCase(btn) {
      const res = await this.fetchFile(
         `/me/library/${encodeURIComponent(this.slug)}/download`, `case:${this.slug}`, btn);
      if (!res) return;
      if (!res.ok) { alert("That case has no file on the platform yet."); return; }
      this.save(res, await res.blob(), `${this.slug}.xlsx`);
   }

   async downloadResource(rid, btn) {
      const res = await this.fetchFile(
         `/me/library/${encodeURIComponent(this.slug)}/resources/${rid}/download`, `res:${rid}`, btn);
      if (!res) return;
      if (!res.ok) {
         // 404 here means not released (the server answers absence, not refusal),
         // and the page is simply out of date: re-read rather than explain.
         this.paint();
         return;
      }
      this.save(res, await res.blob(), "resource");
   }
}

customElements.define("app-library", AppLibrary);
